import type { ReactNode } from 'react';
import { CalendarIcon } from '@phosphor-icons/react';
import { PageHeader } from '@/components/PageHeader';
import { PeriodPicker } from '@/components/PeriodPicker';
import { useSpacePeriod } from '@/hooks/useSpacePeriod';
import type { SpaceDetail } from '@/types/spaces';

// The display face the personal pages give their titles (Categorias, Perfil): a space page is one of them.
const TITLE = 'font-display text-2xl font-semibold tracking-normal sm:text-3xl';

// No sentence under the title and no space name in it: the breadcrumb and the scope switcher already name the space
// (2026-09-23). The period picker is the personal pages' own, in the page rather than the app header (ruled 2026-09-22).
export function SpacePageHeader({ title, space, period = false, children }: Readonly<{
  title: string; space: SpaceDetail; period?: boolean; children?: ReactNode;
}>) {
  // The actions row drops under the title when both do not fit side by side, instead of squeezing either.
  return <PageHeader className="sm:flex-wrap" title={title} titleAs="h1" titleClassName={TITLE}
    actions={children || period ? <div className="flex flex-wrap items-center gap-2 sm:justify-end">{children}<SpacePeriodPicker space={space} visible={period} /></div> : undefined} />;
}

// A trip is one period, so the picker gives way to the same "toda a viagem" pill the app header used to show.
function SpacePeriodPicker({ space, visible }: Readonly<{ space: SpaceDetail; visible: boolean }>) {
  const { selection, onChange } = useSpacePeriod(space.kind);
  if (!visible) return null;
  if (space.kind === 'trip') {
    return <div role="group" aria-label="Período: toda a viagem" className="flex h-9 items-center gap-1.5 rounded-xl border border-border-subtle bg-surface px-3 text-xs font-medium text-text-secondary">
      <CalendarIcon aria-hidden className="size-4 shrink-0" /><span>toda a viagem</span>
    </div>;
  }
  return <PeriodPicker value={selection} onChange={onChange} surface="spaces" align="end" />;
}
